import type { CSSProperties } from 'react';
import {
  describeFactorRange,
  factorRangeGeometry,
  gapText,
  roundScore,
} from './factorRangeGeometry';
import classes from './FactorRange.module.css';

export interface FactorRangeProps {
  /** The company's score for the factor. */
  readonly value: number | null | undefined;
  /** The industry average for the factor. */
  readonly industry: number | null | undefined;
  /** Lowest score seen in the industry. */
  readonly min: number | null | undefined;
  /** Highest score seen in the industry. */
  readonly max: number | null | undefined;
  /** `detailed` (the default) adds the gap to the industry in words. `cell` is the compact form for a dense table cell. */
  readonly size?: 'cell' | 'detailed';
  /** The factor's name, read out with its numbers by assistive technology. */
  readonly label?: string;
  /** Additional CSS class name */
  readonly className?: string;
}

/**
 * One Cowbell Factor against its industry: the company's score as a dot with its value above, the industry average
 * as a tick, red up to the average and green beyond it, and the industry's lowest and highest scores at the ends.
 * A score outside the industry range widens the scale rather than sitting on its edge.
 *
 * With no score it says "Not scored". With a score but no industry average or range to set it against, it prints the score
 * and says there is no industry comparison, rather than drawing a graph from numbers it does not have.
 */
export function FactorRange({
  value,
  industry,
  min,
  max,
  size = 'detailed',
  label,
  className,
}: FactorRangeProps) {
  const input = { value, industry, min, max };
  const name = describeFactorRange(input, label);
  const geometry = factorRangeGeometry(input);
  const rootClass = [classes.root, className].filter(Boolean).join(' ');

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return (
      <span
        role="img"
        aria-label={name}
        data-size={size}
        className={`${classes.empty} ${rootClass}`}
      >
        Not scored
      </span>
    );
  }

  if (!geometry) {
    return (
      <span
        role="img"
        aria-label={name}
        data-size={size}
        className={`${classes.uncompared} ${rootClass}`}
      >
        <span className={classes.plainScore} aria-hidden="true">
          {roundScore(value)}
        </span>
        <span className={classes.note} aria-hidden="true">
          No industry comparison
        </span>
      </span>
    );
  }

  const position = {
    '--factor-range-value': `${geometry.valuePct}%`,
    '--factor-range-industry': `${geometry.industryPct}%`,
    '--factor-range-start': `${geometry.rangeStartPct}%`,
    '--factor-range-end': `${geometry.rangeEndPct}%`,
  } as CSSProperties;

  return (
    <span
      role="img"
      aria-label={name}
      data-size={size}
      data-stance={geometry.stance}
      className={rootClass}
      style={position}
    >
      <span className={classes.value} aria-hidden="true">
        {roundScore(value)}
      </span>
      <span className={classes.track} aria-hidden="true">
        <span className={classes.rail} />
        <span className={classes.below} />
        <span className={classes.above} />
        <span className={classes.tick} />
        <span className={classes.dot} />
      </span>
      <span className={classes.bounds} aria-hidden="true">
        <span className={classes.boundMin}>{roundScore(geometry.min)}</span>
        <span className={classes.boundMax}>{roundScore(geometry.max)}</span>
      </span>
      {size === 'detailed' && (
        <span className={classes.gap} aria-hidden="true">
          {gapText(geometry)}
        </span>
      )}
    </span>
  );
}

FactorRange.displayName = 'FactorRange';

/**
 * What the marks of a `FactorRange` mean, to be put once above a list of them: the dot is the company, the tick is the
 * industry's average, and the bar is the industry's range from its lowest score to its highest, red below the average and
 * green above it.
 */
export function FactorRangeLegend({
  className,
}: Readonly<{ className?: string }>) {
  return (
    <span
      className={[classes.legend, className].filter(Boolean).join(' ')}
      role="group"
      aria-label="How to read the graphs"
    >
      <span className={classes.legendItem}>
        <i className={classes.keyDot} aria-hidden="true" />
        This company
      </span>
      <span className={classes.legendItem}>
        <i className={classes.keyTick} aria-hidden="true" />
        Industry average
      </span>
      <span className={classes.legendItem}>
        <i className={classes.keyBar} aria-hidden="true" />
        Industry range, lowest to highest
      </span>
    </span>
  );
}

FactorRangeLegend.displayName = 'FactorRangeLegend';
